import classNames from 'classnames';
import React from 'react';
import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import FlairIcon from '../../../../../../atomic/atoms/FlairIcon';
import StringToHtml from '../../../../../../components/StringToHtml';
import { GoalBase, GoalId, WeightInfo } from '../types';
import { GoalCardItem } from './GoalCardItem';

export type Props = {
  groupName: string;
  groupDescription?: string;
  goals: GoalBase[];
  onGoalClick: (goalId: GoalId) => void;
  categoryId?: string;
  categoryWeight?: number | null;
  invalidCategoryIds: Set<string>;
  errorsByCategoryId: Map<string, string[]>;
};

const i18prefix = 'performanceReview.goals2.goalStatusCard.';

export const GoalStatusCard: React.FC<Props> = ({
  groupName,
  groupDescription,
  goals,
  onGoalClick,
  categoryId,
  categoryWeight,
  invalidCategoryIds,
  errorsByCategoryId,
}) => {
  const { t } = useTranslation();

  const categoryHasErrors =
    categoryId != null && invalidCategoryIds.has(categoryId);
  const categoryErrors = categoryId
    ? errorsByCategoryId.get(categoryId)
    : undefined;

  return (
    <Card>
      <Card.Body>
        <div className="d-flex justify-content-between mb-3">
          <div className="d-flex align-items-center gap-2">
            <Card.Title as="h3" className="mb-0">
              {groupName}
            </Card.Title>
            {groupDescription && (
              <GroupTooltip
                groupName={groupName}
                groupDescription={groupDescription}
              />
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <GoalsCounter>
              {goals.length +
                ' ' +
                t(i18prefix + 'goal', { count: goals.length })}
            </GoalsCounter>
            {categoryWeight != null && (
              <CategoryWeightBadge
                categoryWeight={categoryWeight}
                categoryHasErrors={categoryHasErrors}
                categoryErrors={categoryErrors}
                groupName={groupName}
              />
            )}
          </div>
        </div>
        <div className={classNames('d-flex', 'flex-column', 'gap-3')}>
          {goals.map((goal) => {
            const weightInfo: WeightInfo | undefined =
              goal.weight != null
                ? {
                    weight: goal.weight,
                    valid: !goal.weightValidationErrors?.length,
                    errorMessages: goal.weightValidationErrors,
                  }
                : undefined;
            return (
              <GoalCardItem
                key={goal.id}
                goal={goal}
                onClick={onGoalClick}
                weightInfo={weightInfo}
              />
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
};

const GroupTooltip = ({
  groupName,
  groupDescription,
}: {
  groupName: string;
  groupDescription: string;
}) => {
  return (
    <OverlayTrigger
      overlay={
        <Tooltip id={`group-description-tooltip-${groupName}`}>
          <StringToHtml text={groupDescription} />
        </Tooltip>
      }
      placement="top">
      <FlairIcon icon="phosphor-info" size="sm" role="button" />
    </OverlayTrigger>
  );
};

const GoalsCounter = ({
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...rest} className={classNames('fs-4', 'fw-bold', className)} />
);

const CategoryWeightBadge: React.FC<{
  categoryWeight: number;
  categoryHasErrors: boolean;
  categoryErrors: string[] | undefined;
  groupName: string;
}> = ({ categoryWeight, categoryHasErrors, categoryErrors, groupName }) => {
  const { t } = useTranslation();
  const isValid = !categoryHasErrors;
  const badge = (
    <WeightBadge isValid={isValid}>
      <FlairIcon icon="phosphor-scales" size="sm" />
      {categoryWeight}%
    </WeightBadge>
  );

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`category-weight-tooltip-${groupName}`}>
          <div>
            {t(i18prefix + 'categoryWeight', { weight: categoryWeight })}
          </div>
          {categoryErrors && categoryErrors.length > 0 && (
            <ul className="list-unstyled mb-0">
              {categoryErrors.map((msg, i) => (
                <li key={i}>• {msg}</li>
              ))}
            </ul>
          )}
        </Tooltip>
      }>
      <span className="d-flex">{badge}</span>
    </OverlayTrigger>
  );
};

const WeightBadge: React.FC<
  React.HTMLAttributes<HTMLSpanElement> & { isValid: boolean }
> = ({ isValid, className, ...rest }) => (
  <span
    {...rest}
    className={classNames(
      'd-flex',
      'align-items-center',
      'gap-1',
      'fs-4',
      'fw-bold',
      !isValid && 'text-warning',
      className,
    )}
  />
);
